import React, { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@material-ui/styles';
import PropTypes from 'prop-types';
import Badge from '@material-ui/core/Badge';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';

import {
    ContainedButton,
    MicIcon,
    VideoIcon,
    PhoneEndIcon,
    MicOffIcon,
    VideoOffIcon,
    GroupIcon2,
    MessageIcon2,
    InfoCirCleIconPermission,
} from '../../index';

import { StyledOpenCallControllerContainer } from './style';
import { PermissionDialog } from '../../Dialog';
import apiService from '../../../service';

export const CallController = ({ isProvider, handleMic, handleCamera, onEndCall }) => {
    const user = useSelector((state) => state.user);
    const [openModalPermission, setOpenModalPermission] = useState(false);
    const [cameraPermission, setCameraPermission] = useState('denied');
    const [microphonePermission, setMicrophonePermission] = useState('denied');
    const me = useSelector((state) => state.me);

    const callServiceLog = async (result) => {
        const newData = {
            ...result,
            user: user,
            sessionType: 'ONE_ON_ONE',
            status: result?.status || '',
        };
        const data = await apiService.sendLoggerPermission(newData);
    };

    const theme = useTheme();

    const audioProducer = useSelector((state) =>
        Object.values(state.producers).find((producer) => producer.track.kind === 'audio')
    );
    const micState = useMemo(() => {
        let newMicState;
        if (!me.canSendMic) newMicState = 'unsupported';
        else if (!audioProducer) newMicState = 'unsupported';
        else if (!audioProducer.paused) newMicState = 'on';
        else newMicState = 'off';

        return newMicState;
    }, [me, audioProducer]);

    const videoProducer = useSelector((state) =>
        Object.values(state.producers).find((producer) => producer.track.kind === 'video')
    );

    const webcamState = useMemo(() => {
        let newWebcamState;
        if (!me.canSendWebcam) newWebcamState = 'unsupported';
        else if (videoProducer && videoProducer.type !== 'share') newWebcamState = 'on';
        else newWebcamState = 'off';

        return newWebcamState;
    }, [me, videoProducer]);

    useEffect(() => {
          console.log('checkPermissions()2 | calling getUserMedia()');
        const checkPermissions = async () => {
            try {
                if (navigator.permissions && navigator.permissions.query) {
                    // Check camera permission
                    const cameraResult = await navigator.permissions
                        .query({ name: 'camera' })
                        .catch(() => null);
                    if (cameraResult) {
                        const convertResult =
                            cameraResult.state === 'prompt' ? 'denied' : cameraResult.state;
                        await callServiceLog({ type: 'camera', permission: convertResult });
                        setCameraPermission(convertResult);
                        cameraResult.onchange = () => setCameraPermission(convertResult);
                    } else {
                        await getMediaPermissions('camera');
                    }
                    const micResult = await navigator.permissions
                        .query({ name: 'microphone' })
                        .catch(() => null);
                    if (micResult) {
                        const convertMicResult =
                            micResult.state === 'prompt' ? 'denied' : micResult.state;
                        await callServiceLog({ type: 'microphone', permission: convertMicResult });
                        setMicrophonePermission(convertMicResult);
                        micResult.onchange = () => setMicrophonePermission(convertMicResult);
                    } else {
                        // Fallback if microphone permission query is not supported
                        await getMediaPermissions('microphone');
                    }
                } else {
                    // Fallback if Permissions API is not supported
                    await getMediaPermissions('camera');
                    await getMediaPermissions('microphone');
                }
            } catch (error) {
                await callServiceLog({ type: 'camera', permission: 'denied' });
                await callServiceLog({ type: 'microphone', permission: 'denied' });
                console.error('Error checking media permissions:', error);
                setCameraPermission('denied');
                setMicrophonePermission('denied');
            }
        };

        const getMediaPermissions = async (mediaType) => {
            try {
                if (mediaType === 'camera') {
                    // Request access to camera
                    await navigator.mediaDevices.getUserMedia({ video: true });
                    setCameraPermission('granted');
                } else if (mediaType === 'microphone') {
                    // Request access to microphone
                    await navigator.mediaDevices.getUserMedia({ audio: true });
                    setMicrophonePermission('granted');
                }
                await callServiceLog({ type: mediaType, permission: 'granted' });
            } catch (error) {
                if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                    if (mediaType === 'camera') {
                        setCameraPermission('denied');
                    } else if (mediaType === 'microphone') {
                        setMicrophonePermission('denied');
                    }
                    await callServiceLog({ type: mediaType, permission: 'denied' });
                } else {
                    console.error(`Error accessing ${mediaType}:`, error);
                }
            }
        };
        checkPermissions();
        window.addEventListener('focus', checkPermissions);

        return () => {
            window.removeEventListener('focus', checkPermissions);
        };
    }, []);

    return (
        <div
            style={{
                backgroundColor: isProvider ? theme.palette.common.black : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around',
                padding: '15px 50px',
                boxShadow: isProvider
                    ? '0px -2px 4px rgba(174, 180, 210, 0.4), inset 0px -2px 4px rgba(174, 180, 210, 0.2)'
                    : 'none',
                position: isProvider ? 'static' : 'fixed',
                left: isProvider ? 'auto' : 0,
                right: isProvider ? 'auto' : 0,
                bottom: isProvider ? 'auto' : 0,
                flexDirection: 'row',
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'row' }}>
                <PermissionDialog
                    open={openModalPermission}
                    onClose={() => setOpenModalPermission(false)}
                />
                <div style={{ position: 'relative' }}>
                    {microphonePermission === 'denied' && (
                        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 100 }}>
                            <InfoCirCleIconPermission />
                        </div>
                    )}

                    <ContainedButton
                        $icon
                        style={{ width: '48px', height: '48px' }}
                        onClick={async () => {
                            if (microphonePermission === 'denied') {
                                setOpenModalPermission(true);
                            } else {
                                handleMic();
                                await callServiceLog({
                                    type: 'microphone',
                                    status: micState === 'on' ? 'INACTIVE' : 'ACTIVE',
                                    permission: microphonePermission,
                                });
                            }
                        }}
                        $backgroundColor={
                            micState === 'on' && microphonePermission !== 'denied'
                                ? 'primaryBlue'
                                : 'gray7'
                        }
                    >
                        {micState === 'on' && microphonePermission !== 'denied' ? (
                            <MicIcon />
                        ) : (
                            <MicOffIcon fill="purple2" />
                        )}
                    </ContainedButton>
                </div>

                <div style={{ position: 'relative' }}>
                    {cameraPermission === 'denied' && (
                        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 100 }}>
                            <InfoCirCleIconPermission />
                        </div>
                    )}

                    <ContainedButton
                        $icon
                        style={{ width: '48px', height: '48px', position: 'relative' }}
                        onClick={async () => {
                            if (cameraPermission === 'denied') {
                                setOpenModalPermission(true);
                            } else {
                                handleCamera();
                                await callServiceLog({
                                    type: 'camera',
                                    status: webcamState === 'on' ? 'INACTIVE' : 'ACTIVE',
                                    permission: cameraPermission,
                                });
                            }
                        }}
                        $backgroundColor={
                            webcamState === 'on' && cameraPermission !== 'denied'
                                ? 'primaryBlue'
                                : 'gray7'
                        }
                        disabled={me.webcamInProgress || me.shareInProgress}
                    >
                        {webcamState === 'on' && cameraPermission !== 'denied' ? (
                            <VideoIcon />
                        ) : (
                            <VideoOffIcon fill="purple2" />
                        )}
                    </ContainedButton>
                </div>
                <ContainedButton
                    $icon
                    style={{ width: '48px', height: '48px' }}
                    $backgroundColor="pink"
                    onClick={() => onEndCall()}
                >
                    <PhoneEndIcon />
                </ContainedButton>
            </div>
        </div>
    );
};

CallController.propTypes = {
    isProvider: PropTypes.bool.isRequired,
    handleMic: PropTypes.func.isRequired,
    handleCamera: PropTypes.func.isRequired,
    onEndCall: PropTypes.func.isRequired,
};

export const OpenCallController = ({
    handleMic,
    handleCamera,
    onEndCall,
    onParticipants,
    participantsCount,
    onMessages,
    messagesCount,
}) => {
    const user = useSelector((state) => state.user);
    const [openModalPermission, setOpenModalPermission] = useState(false);
    const [cameraPermission, setCameraPermission] = useState('denied');
    const [microphonePermission, setMicrophonePermission] = useState('denied');
    const me = useSelector((state) => state.me);

    const callServiceLog = async (result) => {
        const newData = {
            ...result,
            user: user,
            sessionType: 'OPENCALL',
            status: result?.status || '',
        };
        const data = await apiService.sendLoggerPermission(newData);
    };

    const audioProducer = useSelector((state) =>
        Object.values(state.producers).find((producer) => producer.track.kind === 'audio')
    );
    const micState = useMemo(() => {
        let newMicState;
        if (!me.canSendMic) newMicState = 'unsupported';
        else if (!audioProducer) newMicState = 'unsupported';
        else if (!audioProducer.paused) newMicState = 'on';
        else newMicState = 'off';

        return newMicState;
    }, [me, audioProducer]);

    const videoProducer = useSelector((state) =>
        Object.values(state.producers).find((producer) => producer.track.kind === 'video')
    );

    const webcamState = useMemo(() => {
        let newWebcamState;
        if (!me.canSendWebcam) newWebcamState = 'unsupported';
        else if (videoProducer && videoProducer.type !== 'share') newWebcamState = 'on';
        else newWebcamState = 'off';

        return newWebcamState;
    }, [me, videoProducer]);

    useEffect(() => {
        const checkPermissions = async () => {
            console.log('checkPermissions');
            try {
                if (navigator.permissions && navigator.permissions.query) {
                    // Check camera permission
                    const cameraResult = await navigator.permissions
                        .query({ name: 'camera' })
                        .catch(() => null);
                    if (cameraResult) {
                        const convertResult =
                            cameraResult.state === 'prompt' ? 'denied' : cameraResult.state;
                        await callServiceLog({ type: 'camera', permission: convertResult });
                        setCameraPermission(convertResult);
                        cameraResult.onchange = () => setCameraPermission(convertResult);
                    } else {
                        await getMediaPermissions('camera');
                    }
                    const micResult = await navigator.permissions
                        .query({ name: 'microphone' })
                        .catch(() => null);
                    if (micResult) {
                        const convertMicResult =
                            micResult.state === 'prompt' ? 'denied' : micResult.state;
                        await callServiceLog({ type: 'microphone', permission: convertMicResult });
                        setMicrophonePermission(convertMicResult);
                        micResult.onchange = () => setMicrophonePermission(convertMicResult);
                    } else {
                        // Fallback if microphone permission query is not supported
                        await getMediaPermissions('microphone');
                    }
                } else {
                    // Fallback if Permissions API is not supported
                    await getMediaPermissions('camera');
                    await getMediaPermissions('microphone');
                }
            } catch (error) {
                await callServiceLog({ type: 'camera', permission: 'denied' });
                await callServiceLog({ type: 'microphone', permission: 'denied' });
                console.error('Error checking media permissions:', error);
                setCameraPermission('denied');
                setMicrophonePermission('denied');
            }
        };

        const getMediaPermissions = async (mediaType) => {
            try {
                if (mediaType === 'camera') {
                    // Request access to camera
                    await navigator.mediaDevices.getUserMedia({ video: true });
                    setCameraPermission('granted');
                } else if (mediaType === 'microphone') {
                    // Request access to microphone
                    await navigator.mediaDevices.getUserMedia({ audio: true });
                    setMicrophonePermission('granted');
                }
                await callServiceLog({ type: mediaType, permission: 'granted' });
            } catch (error) {
                if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                    if (mediaType === 'camera') {
                        setCameraPermission('denied');
                    } else if (mediaType === 'microphone') {
                        setMicrophonePermission('denied');
                    }
                    await callServiceLog({ type: mediaType, permission: 'denied' });
                } else {
                    console.error(`Error accessing ${mediaType}:`, error);
                }
            }
        };
        checkPermissions();
        window.addEventListener('focus', checkPermissions);

        return () => {
            window.removeEventListener('focus', checkPermissions);
        };
    }, []);

    return (
        <StyledOpenCallControllerContainer>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
                <PermissionDialog
                    open={openModalPermission}
                    onClose={() => setOpenModalPermission(false)}
                />
                <div style={{ position: 'relative' }}>
                    {microphonePermission === 'denied' && (
                        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 100 }}>
                            <InfoCirCleIconPermission />
                        </div>
                    )}
                    <ContainedButton
                        $icon
                        style={{ width: '48px', height: '48px' }}
                        onClick={async () => {
                            if (microphonePermission === 'denied') {
                                setOpenModalPermission(true);
                            } else {
                                handleMic();
                                await callServiceLog({
                                    type: 'microphone',
                                    status: micState === 'on' ? 'INACTIVE' : 'ACTIVE',
                                    permission: microphonePermission,
                                });
                            }
                        }}
                        $backgroundColor={
                            micState === 'on' && microphonePermission !== 'denied'
                                ? 'primaryBlue'
                                : 'gray7'
                        }
                    >
                        {micState === 'on' && microphonePermission !== 'denied' ? (
                            <MicIcon />
                        ) : (
                            <MicOffIcon fill="purple2" />
                        )}
                    </ContainedButton>
                </div>
                <div style={{ position: 'relative' }}>
                    {cameraPermission === 'denied' && (
                        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 100 }}>
                            <InfoCirCleIconPermission />
                        </div>
                    )}
                    <ContainedButton
                        $icon
                        style={{ width: '48px', height: '48px' }}
                        onClick={async () => {
                            if (cameraPermission === 'denied') {
                                setOpenModalPermission(true);
                            } else {
                                handleCamera();
                                await callServiceLog({
                                    type: 'camera',
                                    status: webcamState === 'on' ? 'INACTIVE' : 'ACTIVE',
                                    permission: cameraPermission,
                                });
                            }
                        }}
                        $backgroundColor={
                            webcamState === 'on' && cameraPermission !== 'denied'
                                ? 'primaryBlue'
                                : 'gray7'
                        }
                        disabled={me.webcamInProgress || me.shareInProgress}
                    >
                        {webcamState === 'on' && cameraPermission !== 'denied' ? (
                            <VideoIcon />
                        ) : (
                            <VideoOffIcon fill="purple2" />
                        )}
                    </ContainedButton>
                </div>
                <ContainedButton
                    $icon
                    style={{ width: '48px', height: '48px' }}
                    $backgroundColor="pink"
                    onClick={() => onEndCall()}
                >
                    <PhoneEndIcon />
                </ContainedButton>
            </div>
            <div>
                <StyledButton onClick={onParticipants}>
                    <StyledBadge badgeContent={participantsCount} overlap="rectangular">
                        <GroupIcon2 width="40" height="40" fill="white" />
                    </StyledBadge>
                </StyledButton>
                <StyledButton onClick={onMessages}>
                    <StyledBadge badgeContent={messagesCount} overlap="rectangular">
                        <MessageIcon2 width="40" height="40" fill="white" />
                    </StyledBadge>
                </StyledButton>
            </div>
        </StyledOpenCallControllerContainer>
    );
};

OpenCallController.propTypes = {
    handleMic: PropTypes.func.isRequired,
    handleCamera: PropTypes.func.isRequired,
    onEndCall: PropTypes.func.isRequired,
    onParticipants: PropTypes.func.isRequired,
    participantsCount: PropTypes.number,
    onMessages: PropTypes.func.isRequired,
    messagesCount: PropTypes.number,
    disabledCameraButton: PropTypes.bool,
};

const StyledBadge = withStyles(() => ({
    badge: {
        color: 'white',
        marginRight: '-5px',
        backgroundColor: '#838383',
    },
}))(Badge);

const StyledButton = withStyles(() => ({
    root: {
        minWidth: '0',
        width: '54px',
        height: '54px',
        borderRadius: '50%',

        margin: '10px',
        padding: '5px',

        color: 'white',
        '&:hover': {
            backgroundColor: '#65697E',
        },
    },
}))(Button);
